import { VIEWPORTS, SLIDE_IDS, PresentationType } from '@constants';
import { ReducerAction } from 'react';
import { ActionTypes, ClientPresentationState, SalesRepPresentationState, SharedPresentationState } from './types';

// Initial reducer state.

const { 
    introduction, 
    yourAnalysis, 
    weCanHelp, 
    termOptions, 
    modification, 
    summary, 
    additionalInfo, 
    companyYouCanTrust,
    clientTestimonials,
    clientPortal
} = SLIDE_IDS;

export const initialSharedState: SharedPresentationState = {
    presentationId: null,
    slideId: 1,
    lastSlideId: null,
    pdfPageNumber: 1,
    selectedTerm: {
        [termOptions]: 24,
        [summary]: 24
    },
    stepsReviewed: {
        [weCanHelp]: [],
        [modification]: [],
        [additionalInfo]: [],
        [companyYouCanTrust]: [],
        [clientTestimonials]: [],
        [clientPortal]: []

    },
    infoItem: '',
    loanCalculations: {
        APR: 0,
        loanAmount: 0,
        loanTerm: 0,
        monthlyPayment: 0,
        totalRepayment: 0,
        estTotalSavings: 0
    },
    viewport: VIEWPORTS.desktop,
    slideWidth: 0,
    availableTerms: [],
    presentationEnded: false,
    showPointer: false,
    language: 'English'
};

export const initialRepState: SalesRepPresentationState = {
    ...initialSharedState,
    clientConnected: false,
    selectedSection: {
        [introduction]: 1,
        [yourAnalysis]: 1,
        [weCanHelp]: 1,
        [termOptions]: 1,
        [modification]: 1,
        [summary]: 1,
        [additionalInfo]: 1
    }
};

export const initialClientState: ClientPresentationState = {
    ...initialSharedState,
    repConnected: false,
    presentationType: PresentationType.SLIDES_PRESENTATION,
    pointerPosition: {
        x: 0, 
        y: 0
    },
    videoControls: {
        play: false,
        pause: false
    },
    scrollTop: {},
    scrollLeft: {}
};

// To-do: DRY out shared reducer actions

export const clientReducer = (state, action) => {
    switch(action.type) {
        case ActionTypes.SET_LANGUAGE:
            return {...state, language: action.language };
        case ActionTypes.SET_REP_CONNECTED:
            return {...state, repConnected: action.connected };
        case ActionTypes.SET_SLIDE_WIDTH:
            return {...state, slideWidth: action.width };
        case ActionTypes.SET_PRESENTATION_TYPE:
            return {...state, presentationType: action.presentationType };
        case ActionTypes.SHOW_POINTER:
            return {...state, showPointer: action.showPointer };
        case ActionTypes.UPDATE_POINTER_POSITION:
            return {...state, pointerPosition: action.position };
        case ActionTypes.SET_AVAILABLE_TERMS:
            return {...state, availableTerms: action.terms };
        case ActionTypes.UPDATE_VIDEO_DATA:
            return {...state, videoControls: action.videoData };
        case ActionTypes.SET_SLIDE_ID:
            return {...state, slideId: action.slideId };
        case ActionTypes.SET_LAST_SLIDE_ID:
            return {...state, lastSlideId: action.lastSlideId }
        case ActionTypes.UPDATE_PDF_PAGE:
            return {...state, pdfPageNumber: action.page };
        case ActionTypes.SELECT_TERM: {
            const { slideId, term } = action;
            const { selectedTerm} = state;

            return {...state, selectedTerm: {
                ...selectedTerm, [slideId]: term
            }};
        }
        case ActionTypes.END_PRESENTATION:
            return {...state, presentationEnded: action.presentationEnded };
        case ActionTypes.SET_VIEWPORT:
            return {...state, viewport: action.viewport };
        case ActionTypes.SET_STEP_REVIEWED: {
            const { id, step } = action;
            const { stepsReviewed } = state;
            const currentSteps = stepsReviewed[id];
            const newState = Array.isArray(step) ? step : [...currentSteps, step];
  
            return {
                ...state, stepsReviewed: {
                    ...stepsReviewed, 
                    [id]: newState
                } 
            };
        }
        case ActionTypes.SELECT_INFO_ITEM: {
            return {...state, infoItem: action.item };
        }
        case ActionTypes.SET_LOAN_CALCULATIONS: 
            return {
                ...state, loanCalculations: action.calculations
            };
        case ActionTypes.SET_LOAN_INPUT: {
            const { name, value } = action; 
            const { loanCalculations } = state;

            return {
                ...state, loanCalculations: {
                    ...loanCalculations, [name]: value
                }
            };
        }
        case ActionTypes.SET_SCROLL_TOP: {
            const { elementId, top } = action;
            const { scrollTop } = state;

            return {
                ...state, scrollTop: {...scrollTop, [elementId]: top }
            };
        }
        case ActionTypes.SET_SCROLL_LEFT: {
            const { left, elementId } = action;
            const { scrollLeft } = state;

            return {
                ...state, scrollLeft: {...scrollLeft, [elementId]: left }
            };
        }
        case ActionTypes.SET_PRESENTATION_ID:
            return { ...state, presentationId: action.presentationId }
        default:
            return state;
      }
};

export const salesRepReducer = (state, action) => {
    switch(action.type) {
        case ActionTypes.SET_LANGUAGE:
            return {...state, language: action.language };
        case ActionTypes.SET_SLIDE_WIDTH:
            return {...state, slideWidth: action.width };
        case ActionTypes.SET_AVAILABLE_TERMS:
            return {...state, availableTerms: action.terms };
        case ActionTypes.SELECT_SECTION:
            return {
                ...state, 
                selectedSection: {
                    ...state.selectedSection, 
                    [action.id]: action.section
                }
            };
        case ActionTypes.SELECT_TERM: {
            const { slideId, term } = action;
            const { selectedTerm } = state;
            return {
                ...state, selectedTerm: {
                    ...selectedTerm, [slideId]: term
                }
            };
        }
        case ActionTypes.SET_SLIDE_ID:
            return {...state, slideId: action.slideId };
        case ActionTypes.SET_LAST_SLIDE_ID:
            return {...state, lastSlideId: action.lastSlideId }
        case ActionTypes.SHOW_POINTER:
            return {...state, showPointer: action.showPointer };
        case ActionTypes.UPDATE_PDF_PAGE:
            return {...state, pdfPageNumber: action.page };
        case ActionTypes.SET_STEP_REVIEWED: {
            const { id, step } = action;
            const { stepsReviewed } = state;
            const currentSteps = stepsReviewed[id];
            const newState = Array.isArray(step) ? step : [...currentSteps, step];
    
            return {
                ...state, stepsReviewed: {
                    ...stepsReviewed, 
                    [id]: newState
                } 
            };
        }
        case ActionTypes.SELECT_INFO_ITEM:
            return {...state, infoItem: action.item };
        case ActionTypes.SET_LOAN_CALCULATIONS: 
            return {
                ...state, loanCalculations: action.calculations
            };
        case ActionTypes.SET_LOAN_INPUT: {
            const { name, value } = action; 
            const { loanCalculations } = state;
            return {
                ...state, loanCalculations: {
                    ...loanCalculations, [name]: value
                }
            };
        }
        case ActionTypes.SET_CLIENT_CONNECTED:
            return {...state, clientConnected: action.connected };
        case ActionTypes.SET_VIEWPORT:
            return {...state, viewport: action.viewport };
        case ActionTypes.END_PRESENTATION:
            return {...state, presentationEnded: action.presentationEnded };
        case ActionTypes.SET_PRESENTATION_ID:
            return { ...state, presentationId: action.presentationId }
        default:
            return state;
    }
};
